import { __decorate } from "tslib";
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { isNight } from '../../internal/theme.js';
import { watch } from '../../internal/watch.js';
import { mobileBreakpoint } from '../../utilities/ui-utils.js';
import componentStyles from '../../styles/component.styles.js';
import { CONTAINER_THEMES, IGNORE_KEYPRESS } from './constants/ContainerConstants.js';
import { ICON_TYPES } from '../icon/constants/IconConstants.js';
import '../accessibility/arc-accessibility.js';
import '../bottombar/arc-bottombar.js';
import '../icon-button/arc-icon-button.js';
export default class ArcContainer extends LitElement {
    constructor() {
        super(...arguments);
        this.theme = CONTAINER_THEMES.auto;
        this.fullscreen = false;
        this.getTheme = (date) => (isNight(date) ? CONTAINER_THEMES.dark : CONTAINER_THEMES.light);
        this.handleAccessibilityChange = (event) => {
            const { preferences } = event.detail;
            const { theme } = preferences;
            if (!!theme && theme in CONTAINER_THEMES) {
                this.theme = theme;
                return;
            }
            this.theme = this._appPreferredTheme;
        };
    }
    handleThemeChange() {
        if (!(this.theme in CONTAINER_THEMES) || CONTAINER_THEMES[this.theme] === CONTAINER_THEMES.auto) {
            this.theme = this.getTheme();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('keypress', this.handleKeyDown.bind(this));
        if (this.theme in CONTAINER_THEMES) {
            this._appPreferredTheme = this.theme;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keypress', this.handleKeyDown.bind(this));
    }
    showAccessibility() {
        this.accessibility.open = true;
    }
    handleKeyDown(event) {
        if (!event.composedPath().some((el) => IGNORE_KEYPRESS.includes(el.tagName))) {
            if (event.key === 'a') {
                event.preventDefault();
                this.accessibility.open = !this.accessibility.open;
            }
        }
    }
    render() {
        return html `
      <div id="main">
        <slot id="nav" name="nav" @arc-show-accessibility=${this.showAccessibility}></slot>
        <div id="container" class=${classMap({ fullscreen: this.fullscreen })}>
          <slot name="side"></slot>
          <div id="content">
            <slot></slot>
          </div>
        </div>
        <arc-accessibility
          id="accessibility"
          @arc-accessibility-change=${this.handleAccessibilityChange}
        ></arc-accessibility>
        <slot name="bottom">
          <arc-bottombar>
            <arc-icon-button name=${ICON_TYPES.home} href="/" label="Return home">Home</arc-icon-button>
            <arc-icon-button
              name=${ICON_TYPES.accessibility}
              label="Accessibility panel"
              @click=${this.showAccessibility}
              >Accessibility</arc-icon-button
            >
          </arc-bottombar>
        </slot>
      </div>
    `;
    }
}
ArcContainer.tag = 'arc-container';
ArcContainer.styles = [
    componentStyles,
    css `
      #main {
        height: 100%;
        display: flex;
        flex-direction: column;
        background: rgb(var(--arc-background-color));
        color: rgb(var(--arc-font-color));
      }

      #container {
        flex: 1 1 100%;
        display: flex;
        overflow: hidden;
        gap: 0;
        padding: 0;
      }

      ::slotted(arc-sidebar) {
        display: none;
      }

      #content {
        flex: 1 1 100%;
        overflow: auto;
        background: rgb(var(--arc-container-color));
      }

      arc-bottombar,
      ::slotted(arc-bottombar) {
        display: block;
      }

      /* Medium devices and up */
      @media (min-width: ${mobileBreakpoint}rem) {
        #container:not(.fullscreen) {
          gap: var(--arc-spacing-normal);
          padding: var(--arc-spacing-normal) var(--arc-spacing-medium);
        }

        ::slotted(arc-sidebar) {
          display: block;
        }

        arc-bottombar,
        ::slotted(arc-bottombar) {
          display: none;
        }
      }
    `,
];
__decorate([
    query('#main')
], ArcContainer.prototype, "container", void 0);
__decorate([
    query('#accessibility')
], ArcContainer.prototype, "accessibility", void 0);
__decorate([
    property({ type: String, reflect: true })
], ArcContainer.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], ArcContainer.prototype, "fullscreen", void 0);
__decorate([
    watch('theme')
], ArcContainer.prototype, "handleThemeChange", null);
